import './FaqLayout.scss';

import { Suspense, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router';

import { ROUTES } from '@/components/Shared/routes/routes';
import SuspenseFallback from '@/components/Shared/routes/suspense-fallback';
import { AllurLoader } from '@/components/Shared/ui';
import ErrorTemplate from '@/components/Shared/widgets/errors/error-template';
import { filterFaqSections } from '@/components/Shared/widgets/faq-main-page/helpers/faqHelpers';
import {
  faqMapperToMenuItemList,
  faqMapperToSections
} from '@/components/Shared/widgets/faq-main-page/helpers/faqMapper';
import { DictTypes } from '@/constants';
import { isDictHasAll } from '@/services/helpers/dict';
import { useAppDispatch, useAppSelector } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';
import { getFaqList, setFaqSections } from '@/store/faq.slice';
import { setFaqMenu } from '@/store/userInfo.slice';

import { Breadcrumbs, Header, Navbar } from './widgets';

const FaqLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const { dicts } = useAppSelector((state) => state.dict);
  const { faqList } = useAppSelector((state) => state.faq);
  const { userInfo } = useAppSelector((state) => state.userInfo);
  const { fullscreenCount, fullscreenBlurCount, layoutBlurCount, layoutCount } =
    useAppSelector((state) => state.loading);

  const hasFullscreenLoading: boolean =
    fullscreenCount > 0 || fullscreenBlurCount > 0;

  const faqPagesTypes = useMemo(() => {
    if (!isDictHasAll(dicts, [DictTypes.FaqPagesTypes])) return [];
    return dicts[DictTypes.FaqPagesTypes].content;
  }, [dicts]);

  useEffect(() => {
    if (!userInfo || !isDictHasAll(dicts, [DictTypes.Positions])) return;
    const positionUuid =
      dicts[DictTypes.Positions].getBy.code[userInfo.position.code]?.uuid;
    if (!positionUuid) return;

    const computedFaqSections = filterFaqSections(
      faqMapperToSections(faqList, faqPagesTypes, false),
      {
        filterPositionUuid: positionUuid
      }
    );

    dispatch(setFaqSections(computedFaqSections));
    dispatch(setFaqMenu(faqMapperToMenuItemList(computedFaqSections)));

    return () => {
      dispatch(setFaqMenu([]));
      dispatch(setFaqSections([]));
    };
  }, [faqList, faqPagesTypes, userInfo, dicts]);

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: 'Поддержка',
        helmetTitle: 'Поддержка',
        hasBackArrow: true,
        backArrowTo: ROUTES.autoCredit
      })
    );

    dispatch(getFaqList());

    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, []);

  return (
    <div className="faq-layout">
      {hasFullscreenLoading &&
        (fullscreenBlurCount > 0 ? (
          <AllurLoader fullScreen blurMode />
        ) : (
          fullscreenCount > 0 && <AllurLoader fullScreen />
        ))}

      <Navbar />

      <div className="faq-layout__wrapper">
        <Header />
        <Breadcrumbs />
        <main
          className={`faq-layout__main ${layoutCount > 0 || layoutBlurCount > 0 ? 'faq-layout__main--loading' : ''}`}
        >
          {!hasFullscreenLoading &&
            (layoutBlurCount > 0 ? (
              <AllurLoader layout blurMode />
            ) : (
              layoutCount > 0 && <AllurLoader layout />
            ))}

          <ErrorBoundary
            onError={console.error}
            fallback={<ErrorTemplate type="oops" />}
          >
            <Suspense fallback={<SuspenseFallback loadingType="fullscreen" />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default FaqLayout;
